/**
 * FTR-COCKPIT Part 2: DIEGETIC CORE-INSTRUMENT HUD.
 *
 * The core flight instruments (speed + throttle, power pips, ship status) are
 * painted ONTO the cockpit dash/console mesh instead of the flat DOM overlay —
 * cockpit mode only. Three unlit "screen" planes sit flush on the dash + side
 * consoles, each backed by its own offscreen 2D canvas (the CanvasTexture→map
 * pattern from render/cenotaph.ts + render/orbitLines.ts).
 *
 * AAA-21: the screens are unlit MeshBasicMaterial (toneMapped:false), never
 * emissive geometry and never additive — and the whole cockpit renders AFTER the
 * bloom composer (cockpit.ts render() → clearDepth → render), so these surfaces
 * read as faint painted phosphor, not glow. Functions ≤50 lines; sim-core
 * untouched. instrumentModel() is pure (no three.js) so it stays unit-testable.
 */
import * as THREE from 'three';
import { PIP_MAX } from '@blackmarble/sim-core';
import { fmtSpeed } from '../hud/format.js';
import type { HudFrame } from '../hud/hud.js';
import type { CockpitGeometry, InstrumentBay } from './cockpitConfig.js';

// Design-system palette (mirrors hud.css tokens) — drawn as plain CSS color
// strings on the 2D canvas. Faint, clean, monospace; phosphor cyan rails.
const CYAN = '#3fd0e0'; // --bm-primary: active rails/bars
const WHITE = '#ffffff'; // value numerals
const INK = '#aab0b4'; // --bm-cockpit-ink: dim labels
const ENERGY = '#c97bff'; // --bm-energy: the energy bar
const WARNING = '#ffb000'; // --bm-warning: low
const ALERT = '#ff6b6b'; // contradiction/critical
const FONT = '"Courier New", monospace';
const RAIL = 'rgba(170,176,180,0.45)';

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

/** Derived display values for the diegetic screens (no three.js — unit-testable). */
export interface InstrumentModel {
  speed: string;
  throttle01: number;
  blue: { lo: number; hi: number; inZone: boolean };
  pips: { sys: number; eng: number; wep: number };
  ship: { name: string; hull01: number; shield01: number; fuel01: number; energy01: number };
}

/** PURE: map a HudFrame to the instrument display model. Fractions clamp to 0..1. */
export function instrumentModel(frame: HudFrame): InstrumentModel {
  return {
    speed: fmtSpeed(frame.speedKmS).toUpperCase(),
    throttle01: clamp01(Math.abs(frame.throttle)),
    blue: { lo: clamp01(frame.blueLo), hi: clamp01(frame.blueHi), inZone: frame.inBlueZone },
    pips: {
      sys: clamp01(frame.pips.sys / PIP_MAX),
      eng: clamp01(frame.pips.eng / PIP_MAX),
      wep: clamp01(frame.pips.wep / PIP_MAX),
    },
    ship: {
      name: frame.shipName,
      hull01: clamp01(frame.hull01),
      shield01: clamp01(frame.shield01),
      fuel01: clamp01(frame.fuel01),
      energy01: clamp01(frame.energy01),
    },
  };
}

// --- consistency kit: shared type scale + backing ----------------------------
// All canvases are rasterised at the SAME texel density (PX_PER_UNIT below), so a
// fixed px font/padding renders to the SAME world size on every bay — the three
// readouts share one type scale + backing and read as one matched set.
const PX_PER_UNIT = 1900;
const PAD = 12; // content inset (canvas px)
const LABEL_PX = 13; // dim recessed labels
const VALUE_PX = 20; // secondary values (ship name / row values)
const HERO_PX = 22; // primary value (velocity) — value leads, label recedes (compact panel)

/** Shared inset-screen backing: dark-glass phosphor fill, a thin cyan rail border,
 *  and a DOUBLE-BEZEL dim inner frame line so every screen reads recessed. */
function panel(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = 'rgba(8,14,16,0.34)';
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = 'rgba(63,208,224,0.30)';
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, w - 2, h - 2);
  ctx.strokeStyle = 'rgba(170,176,180,0.22)'; // double-bezel inner line
  ctx.lineWidth = 1;
  ctx.strokeRect(5, 5, w - 10, h - 10);
}

/** Shared dim recessed label (grey, small) — labels recede, values lead. */
function label(ctx: CanvasRenderingContext2D, text: string, x: number, y: number): void {
  ctx.fillStyle = INK;
  ctx.font = `${LABEL_PX}px ${FONT}`;
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(text, x, y);
}

/** A labelled horizontal fill bar (rail outline + coloured fill to `frac`). */
function bar(
  ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number,
  frac: number, color: string,
): void {
  ctx.strokeStyle = RAIL;
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y, w, h);
  ctx.fillStyle = color;
  ctx.fillRect(x + 1.5, y + 1.5, Math.max(0, (w - 3) * clamp01(frac)), h - 3);
}

/** Low/critical colour ramp: critical red, low amber, else the base colour. */
function statusColor(frac: number, base: string): string {
  if (frac < 0.15) return ALERT;
  if (frac < 0.33) return WARNING;
  return base;
}

/** PIP_MAX-cell segmented row (filled cells = round(frac · PIP_MAX)). */
function cells(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, frac: number): void {
  const n = Math.round(clamp01(frac) * PIP_MAX);
  const gap = 4;
  const cw = (w - gap * (PIP_MAX - 1)) / PIP_MAX;
  for (let i = 0; i < PIP_MAX; i++) {
    const cx = x + i * (cw + gap);
    ctx.strokeStyle = RAIL;
    ctx.lineWidth = 1;
    ctx.strokeRect(cx, y, cw, h);
    if (i < n) {
      ctx.fillStyle = CYAN;
      ctx.fillRect(cx + 1, y + 1, cw - 2, h - 2);
    }
  }
}

/** CENTRE bay (PRIMARY, COMPACT): big velocity value + a slim throttle bar with
 *  the blue optimal band. Value-first, no empty filler — the panel stays compact. */
function drawCentre(ctx: CanvasRenderingContext2D, m: InstrumentModel): void {
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  panel(ctx, w, h);
  label(ctx, 'VELOCITY', PAD, PAD + LABEL_PX);
  ctx.fillStyle = WHITE;
  ctx.font = `bold ${HERO_PX}px ${FONT}`;
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(m.speed, PAD, h - 16);
  const bx = PAD;
  const by = h - 9;
  const bw = w - 2 * PAD;
  bar(ctx, bx, by, bw, 6, m.throttle01, CYAN);
  const lo = bx + bw * m.blue.lo;
  const hi = bx + bw * m.blue.hi;
  ctx.globalAlpha = m.blue.inZone ? 0.95 : 0.5;
  ctx.strokeStyle = m.blue.inZone ? WHITE : CYAN;
  ctx.lineWidth = 2;
  ctx.strokeRect(lo, by - 2, Math.max(2, hi - lo), 10);
  ctx.globalAlpha = 1;
}

/** Evenly distribute `n` rows in the canvas below a header band; returns row tops. */
function rowYs(h: number, n: number, headerPx: number): number[] {
  const top = PAD + headerPx + 8;
  const step = (h - top - PAD) / n;
  return Array.from({ length: n }, (_, i) => top + i * step);
}

/** LEFT bay (secondary): SYS / ENG / WEP power pips. */
function drawLeft(ctx: CanvasRenderingContext2D, m: InstrumentModel): void {
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  panel(ctx, w, h);
  label(ctx, 'POWER', PAD, PAD + LABEL_PX);
  const rows: [string, number][] = [['SYS', m.pips.sys], ['ENG', m.pips.eng], ['WEP', m.pips.wep]];
  const ys = rowYs(h, rows.length, LABEL_PX);
  rows.forEach(([name, frac], i) => {
    const y = ys[i]!;
    label(ctx, name, PAD, y + 12);
    cells(ctx, PAD + 42, y, w - PAD * 2 - 42, 14, frac);
  });
}

/** RIGHT bay (secondary, equal-sized): ship name + hull/shield/fuel/energy bars. */
function drawRight(ctx: CanvasRenderingContext2D, m: InstrumentModel): void {
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  panel(ctx, w, h);
  ctx.fillStyle = WHITE;
  ctx.font = `bold ${VALUE_PX}px ${FONT}`;
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(m.ship.name.toUpperCase(), PAD, PAD + VALUE_PX - 2);
  const rows: [string, number, string][] = [
    ['HULL', m.ship.hull01, CYAN], ['SHLD', m.ship.shield01, CYAN],
    ['FUEL', m.ship.fuel01, CYAN], ['ENGY', m.ship.energy01, ENERGY],
  ];
  const ys = rowYs(h, rows.length, VALUE_PX);
  rows.forEach(([name, frac, base], i) => {
    const y = ys[i]!;
    label(ctx, name, PAD, y + 12);
    bar(ctx, PAD + 44, y, w - PAD * 2 - 44, 12, frac, statusColor(frac, base));
  });
}

// --- screen plane construction ----------------------------------------------

interface ScreenSpec {
  cw: number; // canvas px width
  ch: number; // canvas px height
  pw: number; // plane width (cockpit units)
  ph: number; // plane height (cockpit units) — derived from the bay opening
  pos: readonly [number, number, number];
  rot: readonly [number, number, number];
  draw: (ctx: CanvasRenderingContext2D, m: InstrumentModel) => void;
  key: (m: InstrumentModel) => string;
}

interface Screen {
  ctx: CanvasRenderingContext2D | null;
  texture: THREE.CanvasTexture;
  draw: (ctx: CanvasRenderingContext2D, m: InstrumentModel) => void;
  key: (m: InstrumentModel) => string;
  last: string;
}

/** Build one unlit screen plane + its offscreen canvas/texture, placed by spec. */
function buildScreen(spec: ScreenSpec): { screen: Screen; mesh: THREE.Mesh } {
  const canvas = document.createElement('canvas');
  canvas.width = spec.cw;
  canvas.height = spec.ch;
  // jsdom THROWS "Not implemented" from getContext (no `canvas` pkg) rather than
  // returning null, so guard it — a null ctx then skips drawing (tests still build).
  let ctx: CanvasRenderingContext2D | null = null;
  try { ctx = canvas.getContext('2d'); } catch { ctx = null; }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  // depthTest stays ON: the bay housing surrounds/backs the screen, so nothing
  // clips it. polygonOffset pulls the plane a hair toward the eye to win the
  // z-fight against the inset back-plate it rides on (no forward-offset drift).
  const material = new THREE.MeshBasicMaterial({
    map: texture, transparent: true, toneMapped: false, depthWrite: false, depthTest: true,
    polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(spec.pw, spec.ph), material);
  mesh.position.set(spec.pos[0], spec.pos[1], spec.pos[2]);
  // YXZ order = Ry·Rx, matching cockpit.ts transform() (rotateX then rotateY) so the
  // screen's recline+toe agree exactly with its bay housing.
  mesh.rotation.order = 'YXZ';
  mesh.rotation.set(spec.rot[0], spec.rot[1], spec.rot[2]);
  mesh.frustumCulled = false;
  mesh.renderOrder = 3;
  return { screen: { ctx, texture, draw: spec.draw, key: spec.key, last: '' }, mesh };
}

// Per-screen change keys: redraw a canvas only when its own values change.
const centreKey = (m: InstrumentModel): string =>
  `${m.speed}|${m.throttle01.toFixed(3)}|${m.blue.lo.toFixed(3)}|${m.blue.hi.toFixed(3)}|${m.blue.inZone}`;
const leftKey = (m: InstrumentModel): string =>
  `${m.pips.sys.toFixed(3)}|${m.pips.eng.toFixed(3)}|${m.pips.wep.toFixed(3)}`;
const rightKey = (m: InstrumentModel): string =>
  `${m.ship.name}|${m.ship.hull01.toFixed(3)}|${m.ship.shield01.toFixed(3)}|` +
  `${m.ship.fuel01.toFixed(3)}|${m.ship.energy01.toFixed(3)}`;

/** Seat a screen plane FLUSH inside its bay: sized to fit just within the bezel
 *  opening, recessed a hair in front of the inset back-plate (still behind the
 *  bezel lip → reads recessed, never proud), reclined+toed to match the bay. The
 *  local→world maths use Euler 'YXZ' (= Ry·Rx) so they agree with the bay housing. */
function baySeat(b: InstrumentBay): {
  pos: [number, number, number]; rot: [number, number, number]; pw: number; ph: number;
} {
  const margin = 0.006; // keep the screen just inside the bezel opening (no overlap)
  const pw = b.width - 2 * b.bezelThickness - margin;
  const ph = b.height - 2 * b.bezelThickness - margin;
  // Seat the screen in FRONT of the inset back-plate's front face, not its centre.
  // cockpit.ts builds that plate as a 0.012-thick box centred at z=-pocketDepth, so
  // its front face is at -pocketDepth + 0.006; the screen must clear that half-
  // thickness (+ a hair) or the opaque matte plate occludes the unlit screen. Still
  // well behind the bezel lip (stands ~0.014 proud), so it reads recessed.
  const zLocal = -b.pocketDepth + 0.01;
  const local = new THREE.Vector3(0, 0, zLocal).applyEuler(new THREE.Euler(b.pitch, b.yaw, 0, 'YXZ'));
  return {
    pos: [b.x + local.x, b.y + local.y, b.z + local.z],
    rot: [b.pitch, b.yaw, 0], pw, ph,
  };
}

/** Build one screen spec seated in `bay`, rasterised at the shared texel density. */
function bayScreen(
  bay: InstrumentBay,
  draw: (ctx: CanvasRenderingContext2D, m: InstrumentModel) => void,
  key: (m: InstrumentModel) => string,
): ScreenSpec {
  const s = baySeat(bay);
  return {
    cw: Math.round(s.pw * PX_PER_UNIT), ch: Math.round(s.ph * PX_PER_UNIT),
    pw: s.pw, ph: s.ph, pos: s.pos, rot: s.rot, draw, key,
  };
}

/** Seat the three screens in their matched recessed bays (centre first so it stays
 *  index 0). Each screen derives its pos/rot/size from its bay — the housing and
 *  the screen are authored from one source, so they can never drift apart. */
function buildSpecs(g: CockpitGeometry): ScreenSpec[] {
  return [
    bayScreen(g.bays.centre, drawCentre, centreKey),
    bayScreen(g.bays.left, drawLeft, leftKey),
    bayScreen(g.bays.right, drawRight, rightKey),
  ];
}

/**
 * Three diegetic instrument planes parented in one group. The group is added to
 * the cockpit cabin (head-locked, rendered in the existing cockpit pass — no new
 * render pass). update() redraws a canvas only when its values change.
 */
export class CockpitInstruments {
  private readonly group = new THREE.Group();
  private readonly screens: Screen[] = [];

  constructor(geometry: CockpitGeometry) {
    for (const spec of buildSpecs(geometry)) {
      const { screen, mesh } = buildScreen(spec);
      this.screens.push(screen);
      this.group.add(mesh);
    }
  }

  /** All three screen planes in one group (added to the cabin by CockpitView). */
  getGroup(): THREE.Group {
    return this.group;
  }

  /** Recompute the model and redraw only the screens whose values changed. */
  update(frame: HudFrame): void {
    const model = instrumentModel(frame);
    for (const screen of this.screens) {
      const key = screen.key(model);
      if (key === screen.last) continue;
      screen.last = key;
      if (screen.ctx) screen.draw(screen.ctx, model);
      screen.texture.needsUpdate = true;
    }
  }

  setVisible(visible: boolean): void {
    this.group.visible = visible;
  }
}
